import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import {
  FLOWER_SECTIONS,
  PLACEHOLDERS,
  type CategoryId,
  type FlowerSection,
  type ProductPlaceholder,
  type QuantityPrice,
} from "@/data/catalog";
import { ADMIN_AUTH_EVENT, ADMIN_LOGOUT_EVENT, SYNC_CLEARED_EVENT } from "@/constants/admin";
import { hasAdminSession, scheduleCloudPut } from "@/lib/cloud-admin-sync";

export const CATALOG_STORAGE_KEY = "jmanatx_catalog_overrides";

export type ProductEdit = {
  name?: string;
  tiers?: QuantityPrice[];
  flavors?: string[];
  /** Set to `null` to clear the image (override built-in default). */
  imageUrl?: string | null;
};

export type CatalogOverrides = {
  edits: Record<string, ProductEdit>;
  removed: string[];
  /** Slugs removed permanently (hidden + custom row dropped); cleared only by re-adding the same slug or Reset all. */
  purged: string[];
  added: {
    categoryKey: string;
    product: ProductPlaceholder;
  }[];
};

type CatalogCtx = {
  flowerSections: FlowerSection[];
  placeholders: Record<Exclude<CategoryId, "flower">, ProductPlaceholder[]>;
  findProduct: (slug: string) => ProductPlaceholder | undefined;
  updateProduct: (slug: string, changes: ProductEdit) => void;
  removeProduct: (slug: string) => void;
  /** Remove from menu and from “hidden” list; cannot be restored. */
  purgeProduct: (slug: string) => void;
  restoreProduct: (slug: string) => void;
  addProduct: (categoryKey: string, product: ProductPlaceholder) => void;
  isRemoved: (slug: string) => boolean;
  /** Slugs in `removed` for this category (admin restore list). */
  getHiddenProductsForGroup: (categoryKey: string) => ProductPlaceholder[];
  overrides: CatalogOverrides;
  resetCatalog: () => void;
};

const emptyOverrides: CatalogOverrides = { edits: {}, removed: [], purged: [], added: [] };

function loadOverrides(): CatalogOverrides {
  try {
    const raw = localStorage.getItem(CATALOG_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        edits: parsed.edits ?? {},
        removed: parsed.removed ?? [],
        purged: parsed.purged ?? [],
        added: parsed.added ?? [],
      };
    }
  } catch {
    /* corrupt data */
  }
  return { ...emptyOverrides };
}

function persist(o: CatalogOverrides) {
  localStorage.setItem(CATALOG_STORAGE_KEY, JSON.stringify(o));
}

function applyEdit(p: ProductPlaceholder, edit?: ProductEdit): ProductPlaceholder {
  if (!edit) return p;
  return {
    ...p,
    name: edit.name ?? p.name,
    tiers: edit.tiers ?? p.tiers,
    flavors: edit.flavors ?? p.flavors,
    imageUrl: edit.imageUrl === undefined ? p.imageUrl : edit.imageUrl ?? undefined,
  };
}

function isPurged(o: CatalogOverrides, slug: string): boolean {
  return o.purged.includes(slug);
}

function buildFlowerSections(o: CatalogOverrides): FlowerSection[] {
  const sections = FLOWER_SECTIONS.map((section) => ({
    title: section.title,
    strains: section.strains
      .filter((p) => !o.removed.includes(p.slug) && !isPurged(o, p.slug))
      .map((p) => applyEdit(p, o.edits[p.slug])),
  }));

  for (const a of o.added) {
    if (o.removed.includes(a.product.slug) || isPurged(o, a.product.slug)) continue;
    if (!a.categoryKey.startsWith("flower:")) continue;
    const sectionTitle = a.categoryKey.replace("flower:", "");
    const section = sections.find((s) => s.title === sectionTitle);
    if (section) {
      section.strains.push(applyEdit(a.product, o.edits[a.product.slug]));
    }
  }

  return sections;
}

function buildPlaceholders(
  o: CatalogOverrides,
): Record<Exclude<CategoryId, "flower">, ProductPlaceholder[]> {
  const keys = Object.keys(PLACEHOLDERS) as Exclude<CategoryId, "flower">[];
  const result = {} as Record<Exclude<CategoryId, "flower">, ProductPlaceholder[]>;

  for (const key of keys) {
    result[key] = PLACEHOLDERS[key]
      .filter((p) => !o.removed.includes(p.slug) && !isPurged(o, p.slug))
      .map((p) => applyEdit(p, o.edits[p.slug]));

    for (const a of o.added) {
      if (o.removed.includes(a.product.slug) || isPurged(o, a.product.slug)) continue;
      if (a.categoryKey === key) {
        result[key].push(applyEdit(a.product, o.edits[a.product.slug]));
      }
    }
  }

  return result;
}

const Ctx = createContext<CatalogCtx | null>(null);

export function CatalogProvider({ children }: { children: ReactNode }) {
  const initialLocal = useRef(loadOverrides());
  const [overrides, setOverrides] = useState<CatalogOverrides>(() => initialLocal.current);
  const putTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [remoteReady, setRemoteReady] = useState(false);
  const [authTick, setAuthTick] = useState(0);
  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;

  useEffect(() => {
    const onAuth = () => setAuthTick((n) => n + 1);
    const onLogout = () => clearTimeout(putTimer.current);
    window.addEventListener(ADMIN_AUTH_EVENT, onAuth);
    window.addEventListener(ADMIN_LOGOUT_EVENT, onLogout);
    return () => {
      window.removeEventListener(ADMIN_AUTH_EVENT, onAuth);
      window.removeEventListener(ADMIN_LOGOUT_EVENT, onLogout);
    };
  }, []);

  useEffect(() => {
    function onCloudSynced() {
      const o = overridesRef.current;
      initialLocal.current = {
        edits: { ...o.edits },
        removed: [...o.removed],
        purged: [...o.purged],
        added: [...o.added],
      };
    }
    window.addEventListener(SYNC_CLEARED_EVENT, onCloudSynced);
    return () => window.removeEventListener(SYNC_CLEARED_EVENT, onCloudSynced);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/catalog", { cache: "no-store" });
        if (!r.ok || cancelled) return;
        const server = (await r.json()) as CatalogOverrides;
        const next: CatalogOverrides = {
          edits: server.edits ?? {},
          removed: server.removed ?? [],
          purged: server.purged ?? [],
          added: server.added ?? [],
        };
        setOverrides((prev) => {
          if (JSON.stringify(prev) === JSON.stringify(initialLocal.current)) return next;
          return prev;
        });
      } catch {
        /* offline or no Functions */
      } finally {
        if (!cancelled) setRemoteReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    persist(overrides);
  }, [overrides]);

  useEffect(() => {
    if (!remoteReady || !hasAdminSession()) return;
    scheduleCloudPut(putTimer, "/api/catalog", {
      edits: overrides.edits,
      removed: overrides.removed,
      purged: overrides.purged,
      added: overrides.added,
    });
    return () => clearTimeout(putTimer.current);
  }, [overrides, remoteReady, authTick]);

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === CATALOG_STORAGE_KEY) {
        try {
          if (e.newValue) {
            const parsed = JSON.parse(e.newValue);
            setOverrides({
              edits: parsed.edits ?? {},
              removed: parsed.removed ?? [],
              purged: parsed.purged ?? [],
              added: parsed.added ?? [],
            });
          } else {
            setOverrides({ ...emptyOverrides });
          }
        } catch {
          /* ignore corrupt data */
        }
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const flowerSections = buildFlowerSections(overrides);
  const placeholders = buildPlaceholders(overrides);

  const findProduct = useCallback(
    (slug: string): ProductPlaceholder | undefined => {
      for (const s of flowerSections) {
        const found = s.strains.find((p) => p.slug === slug);
        if (found) return found;
      }
      for (const cat of Object.values(placeholders)) {
        const found = cat.find((p) => p.slug === slug);
        if (found) return found;
      }
      return undefined;
    },
    [flowerSections, placeholders],
  );

  const updateProduct = useCallback((slug: string, changes: ProductEdit) => {
    setOverrides((prev) => ({
      ...prev,
      edits: {
        ...prev.edits,
        [slug]: { ...prev.edits[slug], ...changes },
      },
    }));
  }, []);

  const removeProduct = useCallback((slug: string) => {
    setOverrides((prev) => ({
      ...prev,
      removed: prev.removed.includes(slug) ? prev.removed : [...prev.removed, slug],
    }));
  }, []);

  const purgeProduct = useCallback((slug: string) => {
    setOverrides((prev) => {
      const edits = { ...prev.edits };
      delete edits[slug];
      const purged = prev.purged.includes(slug) ? prev.purged : [...prev.purged, slug];
      return {
        ...prev,
        purged,
        removed: prev.removed.filter((s) => s !== slug),
        added: prev.added.filter((a) => a.product.slug !== slug),
        edits,
      };
    });
  }, []);

  const restoreProduct = useCallback((slug: string) => {
    setOverrides((prev) => ({
      ...prev,
      removed: prev.removed.filter((s) => s !== slug),
    }));
  }, []);

  const addProduct = useCallback((categoryKey: string, product: ProductPlaceholder) => {
    setOverrides((prev) => ({
      ...prev,
      purged: prev.purged.filter((s) => s !== product.slug),
      added: [...prev.added, { categoryKey, product }],
    }));
  }, []);

  const isRemoved = useCallback(
    (slug: string) => overrides.removed.includes(slug),
    [overrides.removed],
  );

  const getHiddenProductsForGroup = useCallback(
    (categoryKey: string): ProductPlaceholder[] => {
      const o = overrides;
      const out: ProductPlaceholder[] = [];
      for (const slug of o.removed) {
        if (o.purged.includes(slug)) continue;
        let p: ProductPlaceholder | undefined;
        if (categoryKey.startsWith("flower:")) {
          const title = categoryKey.replace("flower:", "");
          const section = FLOWER_SECTIONS.find((s) => s.title === title);
          p = section?.strains.find((s) => s.slug === slug);
        } else {
          p = PLACEHOLDERS[categoryKey as Exclude<CategoryId, "flower">]?.find((x) => x.slug === slug);
        }
        if (p) {
          out.push(applyEdit(p, o.edits[slug]));
          continue;
        }
        const a = o.added.find((x) => x.product.slug === slug && x.categoryKey === categoryKey);
        if (a) out.push(applyEdit(a.product, o.edits[slug]));
      }
      return out;
    },
    [overrides],
  );

  const resetCatalog = useCallback(() => {
    setOverrides({ ...emptyOverrides });
  }, []);

  return (
    <Ctx.Provider
      value={{
        flowerSections,
        placeholders,
        findProduct,
        updateProduct,
        removeProduct,
        purgeProduct,
        restoreProduct,
        addProduct,
        isRemoved,
        getHiddenProductsForGroup,
        overrides,
        resetCatalog,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useCatalog(): CatalogCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCatalog must be used inside <CatalogProvider>");
  return ctx;
}
